import React, { useMemo } from 'react';
import { Lead } from '../../../../types';
import { AppIcon } from '../../../../components/icons/AppIcon';
import { useCrmData } from '../../../../contexts/CrmProviders';
import { formatPhone } from '../../../../utils/formatters';
import Input from '../../../../components/ui/Input';

interface ContactInfoSectionProps {
    lead: Lead;
    onInputChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void;
}

const rowClass = 'flex items-center gap-s3 md:gap-s4';
const iconClass = 'size-5 shrink-0 text-muted';
const inlineInputClass =
    '!h-auto !min-h-0 !border-none !bg-transparent p-0 font-mono text-base tabular-nums text-secondary !shadow-none focus-visible:!ring-0';

export const ContactInfoSection: React.FC<ContactInfoSectionProps> = ({ lead, onInputChange }) => {
    const { contacts } = useCrmData();

    const contactName = useMemo(() => {
        if (!lead.contactId) return null;
        const contact = contacts.find((c) => c.id === lead.contactId);
        return contact?.name || null;
    }, [lead.contactId, contacts]);

    const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const formatted = formatPhone(e.target.value || '');
        onInputChange({
            ...e,
            target: { ...e.target, name: 'whatsApp', value: formatted },
        });
    };

    return (
        <fieldset className="m-0 space-y-s4 border-none p-0 md:space-y-s5">
            <legend className="sr-only">Informações de contato</legend>

            <div className={rowClass}>
                <AppIcon name="phone" className={iconClass} aria-hidden />
                <Input
                    id="lead-phone"
                    type="tel"
                    name="whatsApp"
                    value={lead.whatsApp || ''}
                    onChange={handlePhoneChange}
                    placeholder="Telefone"
                    maxLength={15}
                    autoComplete="tel"
                    aria-label="Telefone"
                    variant="filled"
                    inputSize="md"
                    containerClassName="min-w-0 flex-1"
                    className={inlineInputClass}
                />
            </div>

            <div className={rowClass}>
                <AppIcon name="mail" className={iconClass} aria-hidden />
                <Input
                    id="lead-email"
                    type="email"
                    name="email"
                    value={lead.email || ''}
                    onChange={onInputChange}
                    placeholder="E-mail"
                    autoComplete="email"
                    aria-label="E-mail"
                    variant="filled"
                    inputSize="md"
                    containerClassName="min-w-0 flex-1"
                    className="!h-auto !min-h-0 !border-none !bg-transparent p-0 font-sans text-base text-secondary !shadow-none focus-visible:!ring-0"
                />
            </div>

            {contactName ? (
                <div className={rowClass}>
                    <AppIcon name="avatar" className={iconClass} aria-hidden />
                    <span className="truncate text-base text-secondary">{contactName}</span>
                </div>
            ) : null}
        </fieldset>
    );
};
